import React, { useState, useEffect } from 'react';
import {
  Button,
  Stack,
  Typography,
  Box,
  IconButton,
  Alert,
  Collapse,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Modal,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  addDocumentWithNameDesc,
  getApplicationFromId,
} from '../../services/ShareableApplicationService';
import CustomDocModal from '../shareableapplication/CustomDocModal';
import MaterialsTable from '../shareableapplication/MaterialsTable';
import DocumentsTable from '../shareableapplication/DocumentsTable';
import PaymentTermsTable from '../shareableapplication/PaymentTermsTable';
import SignWithPAN from '../shareableapplication/SignWithPAN';
import ProjectTracking from '../shareableapplication/ProjectTracking';
import CloseIcon from '@mui/icons-material/Close';
import SignatureCanvas from 'react-signature-canvas';

const CUSTOM_DOCS = 'CUSTOM DOCS';
const PANEL_SERIAL_NUMBER = 'PANEL SERIAL NUMBER';
const INVERTER_SERIAL_NUMBER = 'INVERTER SERIAL NUMBER';
const REQUIRED_DOC_TYPES = [
  'SITE PHOTOS',
  'CHEQUE',
  'ELECTRICITY BILL',
  'IDENTITY',
  PANEL_SERIAL_NUMBER,
  INVERTER_SERIAL_NUMBER,
];
const ShareableApplication = ({ passkey, decodedId }) => {
  const [application, setApplication] = useState(null);
  const [applicationDocuments, setApplicationDocuments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCustomDocModal, setShowCustomDocModal] = useState(false);
  const [signature, setSignature] = useState();
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signAlert, setSignAlert] = useState(false);
  const [isCertified, setIsCertified] = useState(false);
  const [panNumber, setPanNumber] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const documentTypes = applicationDocuments?.map(
    (doc) => doc?.document?.documentType,
  );
  const missingRequiredDocs = REQUIRED_DOC_TYPES.filter(
    (type) => !documentTypes?.includes(type),
  );

  const notAddedCount = missingRequiredDocs.length;

  const submitPAN = (value) => {
    setPanNumber(value);
  };

  const [pan, setPan] = useState('');

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };

  const fetchApplication = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getApplicationFromId(decodedId, passkey);
      setApplication(response.application);
      setApplicationDocuments(response.applicationDocuments);
      if (response === null) {
        setError('Invalid passkey or application not found.');
      }
    } catch (err) {
      setError('Invalid passkey or application not found.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplication();
  }, [decodedId, passkey]);

  const handleUploadSignature = async () => {
    if (!signature || signature.isEmpty()) {
      setError('Please add your signature before uploading.');
      return;
    }

    const signatureUrl = signature.toDataURL('image/png');
    const dataURLtoFile = (dataurl, filename) => {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    };

    const signatureFile = dataURLtoFile(signatureUrl, 'signature.png');
    const date = new Date();
    setLoading(true);
    try {
      const response = await addDocumentWithNameDesc(
        signatureFile,
        decodedId,
        CUSTOM_DOCS,
        'Signature Document',
        application?.consumerDetails?.name +
          ' | ' +
          application?.consumerDetails?.phone +
          ' | ' +
          date.toISOString() +
          ' |  ' +
          panNumber,
        'Signature uploaded',
      );
      setShowSignatureDialog(false);
      fetchApplication();
      alert('Signature uploaded successfully!');
    } catch (err) {
      setError('Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadWithDetails = async (
    event,
    docType,
    name,
    description,
    comment,
  ) => {
    const file = event.target.files[0];
    if (!file) {
      setError('Please select a file.');
      return;
    }

    setLoading(true);
    try {
      const respone = await addDocumentWithNameDesc(
        file,
        decodedId,
        docType,
        name,
        description,
        comment,
      );
      fetchApplication();
      alert(`${docType} uploaded successfully!`);
    } catch (err) {
      setError('Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const [selectedTab, setSelectedTab] = useState('Project tracking');

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
  };

  function ChildModal() {
    return (
      <Button
        disabled={panNumber === ''}
        onClick={() => {
          setShowSignatureDialog(true);
          setSignAlert(true);
          handleClose();
        }}
        fullWidth
        variant='contained'
      >
        sign with pan
      </Button>
    );
  }
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Stack spacing={2} sx={{ mx: 'auto' }} mt={2}>
      {error && <Typography color='error'>{error}</Typography>}
      {application && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
            <Typography variant={'h4'} fontWeight={600}>
              My solar project
            </Typography>

            <Stack display={'flex'} mt={2} spacing={1} direction={'row'}>
              <Chip
                variant={
                  selectedTab === 'Project tracking' ? 'filled' : 'outlined'
                }
                label='Project tracking'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Project tracking')}
              />
              <Chip
                variant={selectedTab === 'Documents' ? 'filled' : 'outlined'}
                label='Documents'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Documents')}
              />
              <Chip
                variant={selectedTab === 'Materials' ? 'filled' : 'outlined'}
                label='Materials'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Materials')}
              />
              <Chip
                variant={selectedTab === 'Pricing' ? 'filled' : 'outlined'}
                label='Pricing'
                clickable
                sx={{ fontWeight: 600, borderRadius: '16px' }}
                onClick={() => handleTabChange('Pricing')}
              />
            </Stack>
          </Box>
        </Box>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='parent-modal-title'
        aria-describedby='parent-modal-description'
      >
        <Box sx={{ ...style, width: 400 }}>
          <SignWithPAN pan={pan} setPan={setPan} panNumber={submitPAN} />
          <ChildModal />
        </Box>
      </Modal>
      {application && (
        <Box display={'flex'} mt={3} pb={6}>
          {selectedTab === 'Documents' && (
            <Stack spacing={2} width={isMobile ? '100%' : '600px'}>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography variant='h5' fontWeight={600}>
                  Documents{' '}
                  <Typography fontWeight={400} display='inline'>
                    {notAddedCount && `(${notAddedCount} left )`}
                  </Typography>
                </Typography>
                <Stack direction={'row'} spacing={2}>
                  <Stack direction='row' spacing={2} alignItems='center'>
                    <Button
                      variant='contained'
                      onClick={() => setShowCustomDocModal(true)}
                    >
                      Add document
                    </Button>

                    {showCustomDocModal && (
                      <CustomDocModal
                        onClose={() => setShowCustomDocModal(false)}
                        onUpload={(event, name, description, comment) =>
                          handleUploadWithDetails(
                            event,
                            CUSTOM_DOCS,
                            name,
                            description,
                            comment,
                          )
                        }
                      />
                    )}
                  </Stack>
                </Stack>
              </Stack>
              <DocumentsTable
                documents={applicationDocuments}
                decodedId={decodedId}
                passkey={passkey}
              />
              <Stack direction='row' spacing={2} justifyContent={'flex-end'}>
                <Button variant='contained' onClick={handleOpen}>
                  Add e-signature
                </Button>
              </Stack>
            </Stack>
          )}
          {selectedTab === 'Materials' && (
            <>
              <MaterialsTable materials={application.materials} />
            </>
          )}
          {selectedTab === 'Pricing' && (
            <>
              <PaymentTermsTable
                paymentTerms={application.paymentTerms}
                pricing={application.pricing}
              />
            </>
          )}
          {selectedTab === 'Project tracking' && (
            <>
              <ProjectTracking
                projectTrackingData={application.projectTracking}
              />
            </>
          )}
        </Box>
      )}
      <Dialog
        open={showSignatureDialog}
        onClose={() => setShowSignatureDialog(false)}
      >
        <Collapse in={signAlert}>
          <Alert
            action={
              <IconButton
                aria-label='close'
                color='inherit'
                size='small'
                onClick={() => {
                  setSignAlert(false);
                }}
              >
                <CloseIcon fontSize='inherit' />
              </IconButton>
            }
          >
            Please sign in the box below
          </Alert>
        </Collapse>
        <DialogTitle>
          <Stack
            direction='row'
            justifyContent={'space-between'}
            alignItems='flex-end'
          >
            <Stack>
              <Typography fontWeight={600}>
                {application?.consumerDetails?.name}'s Signature
              </Typography>
              <Typography color='textSecondary'>
                ({application?.consumerDetails?.email})
              </Typography>
              <Typography color='textSecondary'>
                {application?.consumerDetails?.phone}
              </Typography>
            </Stack>
            <Button onClick={() => signature.clear()}>Clear</Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <SignatureCanvas
            penColor='#00008B'
            canvasProps={{
              width: 500,
              height: 200,
              className: 'sigCanvas',
              style: { border: '1px solid black' },
            }}
            ref={(ref) => {
              setSignature(ref);
            }}
          />
          <Stack direction='row' spacing={1} pt={2} alignItems='flex-start'>
            <Checkbox
              checked={isCertified}
              sx={{ padding: '1px' }}
              onChange={(e) => setIsCertified(e.target.checked)}
            />
            <Typography>
              I certify that this is my ({application?.consumerDetails?.name}
              's) true signature and can be verified using PAN
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSignatureDialog(false)}>Cancel</Button>

          <Button
            onClick={handleUploadSignature}
            variant='contained'
            disabled={!isCertified}
            title={
              !isCertified
                ? 'Self certification mandatory to add signature'
                : ''
            }
          >
            Upload Signature
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ShareableApplication;
